import React, { useEffect, useState, useRef } from 'react';
import { Badge, Button, Col, Container, Row } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DisplayInvoice from './displayInvoice';
import { Invoice, InvoiceStatus } from '../types/invoiceType';
import { Config } from '../config';
import useFirebase from '../hooks/useFirebase';
import { addDoc, collection, doc, updateDoc } from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';
import NetworkService from '../services/networkService';
import SetAppointmentDate from './setAppointmentDate';
import Header from './header';
import { CONSTANTS } from '../constants';

// A custom hook that builds on useLocation to parse
// the query string for you.
function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
}

const PaymentSuccessful = () => {
    const { invoiceId, email } = useParams();
    const query = useQuery();
    const [invoice, setInvoice] = React.useState<Invoice | null>(null);
    const [relatedInvoices, setRelatedInvoices] = useState<Invoice[]>([]);
    const { db, analytics } = useFirebase();
    const [isDepositPayment, setIsDepositPayment] = useState(false);
    const paymentProcessed = useRef(false);
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const connectedAccountId = searchParams.get('connectedAccountId');
    const companyId = searchParams.get('companyId');
    const amountPaid = searchParams.get('amountPaid');
    const isDepositFromUrl = searchParams.get('isDeposit') === 'true';

    useEffect(() => {
        // Check to see if there is a payment_intent value, and if there is then we set the status of this invoice to paid
        if (!invoiceId || paymentProcessed.current) {
            return;
        }

        let paymentIntent = query.get("payment_intent");
        const isDeposit = isDepositFromUrl;
        setIsDepositPayment(isDeposit);

        if (paymentIntent && email) {
            paymentProcessed.current = true;
            const invoiceRef = doc(db, "invoices", invoiceId);

            // Add the email to the payment intent's receipt_email field so that way the email is sent automatically when the payment is successful
            let networkService = new NetworkService();
            networkService.addReceiptEmailToPaymentIntent(paymentIntent, email);

            // If this is a deposit payment for a solar screen estimate
            if (invoice?.isEstimate && isDeposit) {
                updateDoc(invoiceRef, {
                    depositPaid: true,
                    paymentIntent: paymentIntent,
                    email: email,
                    depositPaidDate: new Date()
                });

                // Send receipt email for the deposit payment
                networkService.sendReceipt(invoiceId, email, paymentIntent)
                    .then((result: any) => {
                        console.log("Deposit receipt sent successfully:", result);
                        logEvent(analytics, "deposit_receipt_sent_on_payment", {
                            invoiceId: invoiceId,
                            email: email
                        });
                    })
                    .catch((error: any) => {
                        console.error("Failed to send deposit receipt:", error);
                        // Don't break the payment flow if receipt fails
                        logEvent(analytics, "deposit_receipt_send_failed", {
                            invoiceId: invoiceId,
                            email: email,
                            error: error.message
                        });
                    });

            } else {
                // Regular invoice payment
                updateDoc(invoiceRef, {
                    status: InvoiceStatus.paid,
                    paymentIntent: paymentIntent,
                    email: email
                });

                // Send receipt email for the paid invoice
                networkService.sendReceipt(invoiceId, email, paymentIntent)
                    .then((result: any) => {
                        console.log("Receipt sent successfully:", result);
                        logEvent(analytics, "receipt_sent_on_payment", {
                            invoiceId: invoiceId,
                            email: email
                        });
                    })
                    .catch((error: any) => {
                        console.error("Failed to send receipt:", error);
                        // Don't break the payment flow if receipt fails
                        logEvent(analytics, "receipt_send_failed", {
                            invoiceId: invoiceId,
                            email: email,
                            error: error.message
                        });
                    });
            }

            // Add the email to our email list
            addDoc(collection(db, "email-list"), {
                email: email
            });

            // Log that the invoice was paid
            logEvent(analytics, "invoice_paid", {
                invoiceId: invoiceId,
                paymentIntent: paymentIntent,
                email: email,
                isDeposit: isDeposit
            });
        }
    }, [invoiceId, email, query, db, analytics]);

    return (
        <div className="max-w-4xl mx-auto px-4">
            <Header connectedAccountId={connectedAccountId} companyId={companyId} />

            <Container className="my-5">
                <div className="bg-white rounded-lg p-5 border-start border-success border-4">
                    <Row>
                        <Col xs={12}>
                            <h2 className="text-success font-bold mb-3 d-flex align-items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="me-2" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                                </svg>
                                {isDepositPayment ? 'Deposit Payment Successful!' : 'Payment Successful!'}
                            </h2>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="my-3" xs={12}>
                            {isDepositPayment ? (
                                <>
                                    <p className="mb-4 lead">
                                        {CONSTANTS.LABELS.DEPOSIT_SUCCESS_MESSAGE}
                                    </p>

                                    {amountPaid && invoice && (() => {
                                        const paid = parseFloat(amountPaid);
                                        const totalAfterDiscount = invoice.discountedTotal
                                            || invoice.total
                                            || (invoice.discountPercent && invoice.discountPercent > 0
                                                ? invoice.amount * (1 - invoice.discountPercent / 100) * (invoice.meshType === 80 ? 0.88 : 1)
                                                : invoice.discount && invoice.discount > 0
                                                    ? invoice.amount * (1 - invoice.discount / 100)
                                                    : invoice.discountAmount && invoice.discountAmount > 0
                                                        ? invoice.amount - invoice.discountAmount
                                                        : invoice.amount * (invoice.meshType === 80 ? 0.88 : 1));
                                        const remaining = totalAfterDiscount - paid;
                                        return (
                                            <div className="bg-light p-4 rounded-lg mb-4">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <span className="fw-bold">{CONSTANTS.LABELS.AMOUNT_PAID}</span>
                                                    <span className="text-success fw-bold fs-5">${paid.toFixed(2)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span className="fw-bold">{CONSTANTS.LABELS.REMAINING_BALANCE}</span>
                                                    <span className="text-danger fw-bold fs-5">${remaining.toFixed(2)}</span>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    <p className="mb-2">
                                        <strong>{CONSTANTS.LABELS.DEPOSIT_NEXT_STEPS_TITLE}</strong>
                                    </p>
                                    <ul className="mb-4">
                                        <li className="mb-2">{CONSTANTS.LABELS.DEPOSIT_NEXT_STEP_1}</li>
                                        <li className="mb-2">{CONSTANTS.LABELS.DEPOSIT_NEXT_STEP_2}</li>
                                        <li>{CONSTANTS.LABELS.DEPOSIT_NEXT_STEP_3}</li>
                                    </ul>
                                </>
                            ) : (
                                <p className="mb-4 lead">
                                    Thank you so much for your prompt payment! We appreciate your business, and we look forward to working with you again!
                                </p>
                            )}
                            <p className="text-muted">
                                A receipt will be sent to your email address shortly.
                            </p>
                        </Col>
                    </Row>
                </div>
            </Container>

            {/* {invoice && (
                <Container className="my-4">
                    <Row>
                        <Col>
                            <div className="bg-white rounded-lg shadow p-4">
                                <SetAppointmentDate invoice={invoice} />
                            </div>
                        </Col>
                    </Row>
                </Container>
            )} */}

            {/* Open Invoices Section */}
            {relatedInvoices.length > 0 && (
                <Container className="my-4">
                    <div className="bg-white rounded-lg p-4 border shadow-sm">
                        {(() => {
                            const unpaidInvoices = relatedInvoices.filter(inv => inv.status !== InvoiceStatus.paid);
                            const paidCount = relatedInvoices.length - unpaidInvoices.length;

                            return (
                                <>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0 fw-bold">{CONSTANTS.LABELS.REMAINING_PAYMENTS}</h5>
                                        <Badge bg={unpaidInvoices.length === 0 ? "success" : "primary"}>
                                            {paidCount} of {relatedInvoices.length} paid
                                        </Badge>
                                    </div>

                                    {unpaidInvoices.length === 0 ? (
                                        <div className="text-center py-3">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="text-success mb-2" viewBox="0 0 16 16">
                                                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                                            </svg>
                                            <p className="text-success fw-bold mb-0">{CONSTANTS.LABELS.ALL_PAYMENTS_COMPLETE}</p>
                                        </div>
                                    ) : (
                                        <>
                                            <p className="text-muted mb-3">
                                                {unpaidInvoices.length} {CONSTANTS.LABELS.PAYMENTS_REMAINING_SUMMARY}
                                            </p>
                                            <div className="list-group">
                                                {unpaidInvoices.map((inv) => (
                                                    <div
                                                        key={inv.id}
                                                        className="list-group-item d-flex justify-content-between align-items-center"
                                                    >
                                                        <div>
                                                            <h6 className="mb-0">
                                                                Payment {inv.paymentNumber} of {inv.totalPayments}
                                                            </h6>
                                                            {inv.dueDate && (
                                                                <small className="text-muted">
                                                                    Due: {new Date(inv.dueDate.toDate ? inv.dueDate.toDate() : inv.dueDate).toLocaleDateString()}
                                                                </small>
                                                            )}
                                                            <div>
                                                                <Badge bg={inv.status === InvoiceStatus.pending ? "warning" : "secondary"} className="mt-1">
                                                                    {inv.status === InvoiceStatus.pending ? "Payment Due" : inv.status}
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                        <div className="d-flex align-items-center">
                                                            <span className="fw-bold me-3">${Math.round(inv.amount)}</span>
                                                            <Button
                                                                variant={inv.status === InvoiceStatus.pending ? "primary" : "outline-secondary"}
                                                                size="sm"
                                                                onClick={() => navigate(`/invoice/${inv.id}`)}
                                                            >
                                                                {CONSTANTS.LABELS.PAY_NEXT_INVOICE}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                </Container>
            )}

            {invoiceId && (
                <div className="my-4">
                    <DisplayInvoice
                        invoiceId={invoiceId}
                        setInvoice={setInvoice}
                        onRelatedInvoicesLoaded={setRelatedInvoices}
                    />
                </div>
            )}
        </div>
    );
};

export default PaymentSuccessful;
